import { useAuth } from "@/context/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crown, BarChart3, Mail, GitBranch, Headphones, Check, Lock, Sparkles } from "lucide-react";
import { Link } from "wouter";

const ADDONS = [
  {
    id: "analytics",
    title: "Advanced Analytics",
    description: "Deep-dive reports: cohort analysis, revenue forecasting, product performance trends, and customizable dashboards.",
    icon: BarChart3,
    color: "text-blue-600",
    bg: "bg-blue-50",
    comingSoon: true,
    enterpriseOnly: true,
    proOrEnterpriseOnly: false,
  },
  {
    id: "email",
    title: "Email Notifications",
    description: "Automated low-stock alerts, daily sales summaries, customer receipt emails, and account notifications.",
    icon: Mail,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    comingSoon: false,
    enterpriseOnly: false,
    proOrEnterpriseOnly: false,
  },
  {
    id: "multibranch",
    title: "Multi-Branch Management",
    description: "Manage multiple store locations from one dashboard. Transfer stock, compare branch performance, and unify reports.",
    icon: GitBranch,
    color: "text-violet-600",
    bg: "bg-violet-50",
    comingSoon: false,
    enterpriseOnly: false,
    proOrEnterpriseOnly: true,
  },
  {
    id: "support",
    title: "Priority Support",
    description: "Priority WhatsApp support and onboarding assistance during our published support hours (Mon–Sat, 8am–8pm WAT).",
    icon: Headphones,
    color: "text-amber-600",
    bg: "bg-amber-50",
    comingSoon: false,
    enterpriseOnly: true,
    proOrEnterpriseOnly: false,
  },
];

export default function Addons() {
  const { store } = useAuth();
  const isEnterprise = store?.plan === "enterprise";
  const isProOrEnterprise = store?.plan === "pro" || isEnterprise;
  const upgradePlan = store?.plan === "pro" ? "Enterprise" : "Pro";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Features & Add-ons</h1>
          <p className="text-muted-foreground mt-1">See what is active, included with your plan, or still in development.</p>
        </div>
        {isEnterprise ? (
          <Badge className="bg-amber-700 text-white gap-1 px-3 py-1">
            <Crown className="w-3 h-3" /> Enterprise Active
          </Badge>
        ) : (
          <Link href="/subscribe">
            <Button className="gap-2">
              <Crown className="w-4 h-4" /> Upgrade to {upgradePlan}
            </Button>
          </Link>
        )}
      </div>

      {!isEnterprise && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-4">
          <Crown className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-900">You're on the {store?.plan || "trial"} plan</h3>
            <p className="text-sm text-amber-700 mt-1">
               Upgrade to {upgradePlan} to unlock more features, including {isProOrEnterprise ? "Enterprise-only tools" : "multi-branch management and more staff accounts"}.
            </p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {ADDONS.map(addon => {
          const isLocked = (addon.enterpriseOnly && !isEnterprise)
            || (addon.proOrEnterpriseOnly && !isProOrEnterprise);
          const showComingSoon = addon.comingSoon;

          return (
            <Card key={addon.id} className={`relative overflow-hidden transition-all ${isLocked ? "opacity-75" : ""}`}>
              {/* Top-right badge */}
              <div className="absolute top-3 right-3">
                {showComingSoon ? (
                  <Badge variant="secondary" className="gap-1 text-xs bg-primary/10 text-primary border-primary/20">
                    <Sparkles className="w-3 h-3" /> Coming Soon
                  </Badge>
                ) : isLocked ? (
                  <Lock className="w-4 h-4 text-muted-foreground" />
                ) : addon.proOrEnterpriseOnly ? (
                  <Badge variant="default" className="bg-emerald-500 text-xs gap-1">
                    <Check className="w-3 h-3" /> Included with Pro & Enterprise
                  </Badge>
                ) : (
                  <Badge variant="default" className="bg-emerald-500 text-xs gap-1">
                    <Check className="w-3 h-3" /> Active
                  </Badge>
                )}
              </div>

              <CardHeader>
                <div className={`w-12 h-12 ${addon.bg} rounded-xl flex items-center justify-center mb-3`}>
                  <addon.icon className={`w-6 h-6 ${addon.color}`} />
                </div>
                <CardTitle className="text-lg">{addon.title}</CardTitle>
                <CardDescription>{addon.description}</CardDescription>
              </CardHeader>

              {showComingSoon && (
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 rounded-lg px-3 py-2.5">
                    <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    We're actively building this — you'll be notified when it's ready.
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-bold mb-4">What's included in each plan</h2>
        <div className="overflow-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-4 font-medium">Feature</th>
                <th className="text-center p-4 font-medium">Basic</th>
                <th className="text-center p-4 font-medium">Pro</th>
                <th className="text-center p-4 font-medium text-amber-700">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Product Limit", "1,500", "2,500", "Unlimited"],
                ["Sales & Inventory", "✓", "✓", "✓"],
                ["Purchases & Expenses", "✓", "✓", "✓"],
                ["P&L Reports", "✓", "✓", "✓"],
                ["Basic Reports", "✓", "✓", "✓"],
                ["CSV/Excel Import Limit", "1,500 products", "2,500 products", "Unlimited"],
                ["P&L Export (Excel/PDF)", "—", "✓", "✓"],
                ["Staff Accounts", "3", "7", "Unlimited"],
                ["Attendance Tracking", "—", "—", "✓"],
                ["Checklist Management", "—", "—", "✓"],
                ["PDF/Excel Export", "—", "—", "✓"],
                ["Advanced Analytics", "—", "—", "Coming Soon"],
                ["Email Notifications", "✓", "✓", "✓"],
                ["Multi-Branch", "—", "✓", "✓"],
                ["Priority Support", "—", "—", "✓"],
              ].map(([feature, basic, pro, enterprise]) => (
                <tr key={feature} className="border-b hover:bg-muted/30">
                  <td className="p-4 font-medium">{feature}</td>
                  <td className="p-4 text-center text-muted-foreground">{basic}</td>
                  <td className="p-4 text-center text-muted-foreground">{pro}</td>
                  <td className={`p-4 text-center font-semibold ${enterprise === "Coming Soon" ? "text-primary/70 text-xs" : "text-amber-700"}`}>
                    {enterprise === "Coming Soon" ? (
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Coming Soon
                      </span>
                    ) : enterprise}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
